import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import PageHeader from "../components/PageHeader.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import apiClient from "../services/api.js";

const formatMoney = (value, compact = false) =>
  new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    notation: compact ? "compact" : "standard",
    maximumFractionDigits: compact ? 1 : 2
  }).format(Number(value || 0));

const quickActions = [
  { to: "/contributions", label: "Contribute", icon: "+", tone: "bg-brand/15 text-brandStrong" },
  { to: "/savings", label: "Save", icon: "↗", tone: "bg-mint/20 text-emerald-700" },
  { to: "/loans", label: "Get a loan", icon: "₦", tone: "bg-accent/10 text-accent" },
  { to: "/asset-finance", label: "Finance asset", icon: "◇", tone: "bg-amber-100 text-amber-700" }
];

const services = [
  { to: "/savings", label: "Target savings", copy: "Save toward rent, school fees, or a business goal.", icon: "◎" },
  { to: "/contributions", label: "Daily contributions", copy: "Keep a clear cooperative contribution record.", icon: "↻" },
  { to: "/loans", label: "Member loans", copy: "Apply and follow approval or disbursement status.", icon: "₦" },
  { to: "/asset-finance", label: "Asset finance", copy: "Spread payments for productive and solar assets.", icon: "◇" }
];

export default function DashboardPage() {
  const { member, fetchMe } = useAuth();
  const [contributions, setContributions] = useState([]);
  const [kycEntries, setKycEntries] = useState([]);
  const [cooperative, setCooperative] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        await fetchMe();
        const [contribRes, kycRes, coopRes] = await Promise.all([
          apiClient.get("/contributions/"),
          apiClient.get("/kyc/me"),
          apiClient.get("/cooperative/summary")
        ]);
        if (!active) return;
        setContributions(contribRes.data || []);
        setKycEntries(kycRes.data || []);
        setCooperative(coopRes.data || null);
      } catch {
        if (!active) return;
        setContributions([]);
        setKycEntries([]);
        setCooperative(null);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [fetchMe]);

  const totalContributed = useMemo(
    () => contributions.reduce((sum, entry) => sum + Number(entry.contribution_amount || 0), 0),
    [contributions]
  );
  const latestKyc = kycEntries[0];
  const balance = member?.wallet_balance || "0.00";

  return (
    <div className="space-y-6 fade-up">
      <PageHeader
        title={`Good day, ${member?.first_name || "Member"}`}
        description="Here’s a simple view of your cooperative finances."
        action={
          <Link to="/onboarding" className="hidden rounded-2xl bg-white px-4 py-2 text-xs font-semibold text-accent shadow-sm sm:block">
            My profile
          </Link>
        }
      />

      <section className="relative overflow-hidden rounded-[30px] bg-accent p-5 text-white shadow-xl shadow-accent/15 md:p-7">
        <div className="absolute -right-12 -top-16 h-48 w-48 rounded-full border border-white/10" />
        <div className="absolute right-7 top-5 h-24 w-24 rounded-full bg-mint/10 blur-2xl" />
        <div className="relative">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-white/55">Available wallet balance</p>
            <span className="rounded-full bg-white/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-white/70">Member wallet</span>
          </div>
          <p className="mt-4 text-3xl font-semibold tracking-tight md:text-4xl">{formatMoney(balance)}</p>
          <div className="mt-6 grid grid-cols-2 gap-3 border-t border-white/10 pt-4 text-xs">
            <div>
              <p className="text-white/50">Total contributions</p>
              <p className="mt-1 font-semibold text-white">{formatMoney(totalContributed, true)}</p>
            </div>
            <div className="border-l border-white/10 pl-4">
              <p className="text-white/50">KYC status</p>
              <p className="mt-1 font-semibold capitalize text-white">{latestKyc?.status || "Not submitted"}</p>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="grid grid-cols-4 gap-2 sm:gap-4">
          {quickActions.map((action) => (
            <Link key={action.to} to={action.to} className="group flex min-w-0 flex-col items-center gap-2 rounded-2xl bg-white/70 px-1 py-3 text-center shadow-sm transition active:scale-95 sm:px-3 sm:py-4">
              <span className={`flex h-11 w-11 items-center justify-center rounded-2xl text-lg font-semibold ${action.tone}`}>{action.icon}</span>
              <span className="w-full truncate text-[10px] font-semibold text-slate-600 sm:text-xs">{action.label}</span>
            </Link>
          ))}
        </div>
      </section>

      <section>
        <div className="mb-3 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-400">Cooperative services</p>
            <h2 className="mt-1 text-xl font-semibold text-accent">Grow with your coop</h2>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {services.map((service) => (
            <Link key={service.to} to={service.to} className="card-surface flex items-start gap-4 rounded-3xl p-4 transition active:scale-[0.99] md:p-5">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-accent/10 text-lg font-semibold text-accent">{service.icon}</span>
              <div className="min-w-0">
                <p className="font-semibold text-ink">{service.label}</p>
                <p className="mt-1 text-xs leading-5 text-slate-500">{service.copy}</p>
              </div>
              <span className="ml-auto text-slate-300">›</span>
            </Link>
          ))}
        </div>
      </section>

      <section className="card-surface rounded-[28px] p-5 md:p-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-400">Activity</p>
            <h2 className="mt-1 text-xl font-semibold text-accent">Recent contributions</h2>
          </div>
          <Link to="/contributions" className="text-xs font-semibold text-brandStrong">View all</Link>
        </div>
        <div className="mt-4 space-y-1">
          {contributions.slice(0, 4).map((entry) => (
            <div key={entry.id} className="flex items-center gap-3 border-b border-black/5 py-3 last:border-0">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-mint/15 text-sm font-semibold text-emerald-700">↓</span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-ink">{entry.item_name || "Contribution"}</p>
                <p className="text-xs text-slate-400">Quantity {entry.quantity || 1}</p>
              </div>
              <p className="text-sm font-semibold text-ink">{formatMoney(entry.contribution_amount)}</p>
            </div>
          ))}
          {!loading && contributions.length === 0 && (
            <div className="py-8 text-center">
              <p className="text-sm font-semibold text-ink">No activity yet</p>
              <p className="mt-1 text-xs text-slate-500">Your cooperative transactions will appear here.</p>
              <Link to="/contributions" className="mt-4 inline-flex rounded-2xl bg-accent px-4 py-2 text-xs font-semibold text-white">Make first contribution</Link>
            </div>
          )}
        </div>
      </section>

      {(cooperative?.thrift_plans || cooperative?.loan_products) && (
        <section className="grid gap-3 sm:grid-cols-2">
          <CompactShelf title="Available thrift plans" items={cooperative?.thrift_plans || []} empty="No thrift plans available." />
          <CompactShelf title="Loan products" items={cooperative?.loan_products || []} empty="No loan products available." />
        </section>
      )}
    </div>
  );
}

function CompactShelf({ title, items, empty }) {
  return (
    <div className="card-surface rounded-3xl p-5">
      <h2 className="font-semibold text-accent">{title}</h2>
      <div className="mt-3 space-y-2">
        {items.slice(0, 2).map((item) => (
          <div key={item.id} className="rounded-2xl bg-white/70 p-3">
            <p className="text-sm font-semibold text-ink">{item.name}</p>
            <p className="mt-1 text-xs capitalize text-slate-500">{item.frequency || `${item.tenure_months || "Flexible"} months`}</p>
          </div>
        ))}
        {!items.length && <p className="text-xs text-slate-500">{empty}</p>}
      </div>
    </div>
  );
}
